import { injectScopedStyle, UI_ATTR } from '../../utils/dom';

export const POPOVER_ATTR = UI_ATTR;
export const POPOVER_STYLE_ID = 'dddk-dwell-style';

export function ensureDwellStyles(): void {
  injectScopedStyle(POPOVER_STYLE_ID, `
    [${POPOVER_ATTR}="dwell"] {
      position: absolute; display: none;
      max-width: 300px;
      background: var(--dddk-bg-elevated, #fff);
      border: 1px solid var(--dddk-border, rgba(0,0,0,0.1));
      border-radius: 10px;
      padding: 10px 12px;
      box-shadow: var(--dddk-shadow-md, 0 4px 16px rgba(0,0,0,0.12));
      font-size: 13px;
      color: var(--dddk-text, #111);
      z-index: var(--dddk-z-bar, 9500);
      animation: dddkMpIn 140ms ease-out;
    }
    [${POPOVER_ATTR}="dwell"] .dwell-head {
      display: flex; align-items: flex-start; justify-content: space-between;
      gap: 8px; margin-bottom: 8px;
    }
    /* Mascot duck perches on the popover — turns the dwell selection
       menu into an obvious dddk surface at a glance. Uses a CSS
       variable so the host controls the URL (nothing baked into
       the library). Off by default (empty var). */
    [${POPOVER_ATTR}="dwell"] .dwell-head::before {
      content: '';
      display: var(--dddk-dwell-avatar-display, block);
      width: 22px;
      height: 22px;
      background-image: var(--dddk-avatar-url, none);
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      flex-shrink: 0;
      animation: dddkDwellAvatarBob var(--dddk-dwell-avatar-bob-duration, 2.6s) ease-in-out infinite;
    }
    @keyframes dddkDwellAvatarBob {
      0%, 100% { transform: translateY(0); }
      50%      { transform: translateY(-2px); }
    }
    [${POPOVER_ATTR}="dwell"] .dwell-label { font-weight: 600; flex: 1; }
    [${POPOVER_ATTR}="dwell"] .dwell-close {
      background: transparent; border: 0; cursor: pointer;
      color: var(--dddk-text-muted, #71717a);
      font-size: 14px; line-height: 1; padding: 0 2px;
    }
    [${POPOVER_ATTR}="dwell"] .dwell-close:hover { color: var(--dddk-text, #111); }
    [${POPOVER_ATTR}="dwell"] .dwell-actions {
      display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 6px;
    }
    [${POPOVER_ATTR}="dwell"] .dwell-actions button {
      background: var(--dddk-accent-soft, rgba(236,72,153,0.12));
      color: inherit; border: 0; cursor: pointer;
      padding: 4px 8px; border-radius: 5px; font: inherit; font-size: 12px;
    }
    [${POPOVER_ATTR}="dwell"] .dwell-actions button:hover {
      background: var(--dddk-accent, #ec4899); color: #fff;
    }
    [${POPOVER_ATTR}="dwell"] .dwell-hint {
      font-size: 10px; color: var(--dddk-text-muted, #71717a);
      text-align: right;
      padding-right: 22px; /* clear space for the corner duck badge */
    }
    /* Corner duck badge — the "mascot signed this analysis" mark. Sits
       in the bottom-right of the popover, small, tilted a hair, gently
       winking. Uses the same avatar URL as the header. Absent when the
       host hasn't set the URL. */
    [${POPOVER_ATTR}="dwell"]::after {
      content: '';
      position: absolute;
      right: 8px; bottom: 8px;
      width: 18px; height: 18px;
      background-image: var(--dddk-avatar-url, none);
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      transform: rotate(-8deg);
      pointer-events: none;
      opacity: 0.85;
      animation: dddkDwellCornerWink 3s ease-in-out infinite;
    }
    @keyframes dddkDwellCornerWink {
      0%, 90%, 100% { transform: rotate(-8deg) scale(1); }
      92%           { transform: rotate(-8deg) scale(1.15); }
      96%           { transform: rotate(-8deg) scale(0.95); }
    }
    @keyframes dddkMpIn {
      from { opacity: 0; transform: translateY(-4px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    /* ── Swim overlay — real spritesheet cycle, on the outline ────
       The duck sprite sits AT the outline's y-position (not floating
       above), swims left→right→left at a CONSTANT PIXELS/SEC velocity
       (JS sets --dddk-swim-duration from element width), and cycles
       through a 4-frame spritesheet for a real "kick-swim" feel via
       CSS steps(4). A background strip on the container acts as
       an eraser that visibly breaks the outline where the duck is,
       reading as "duck IS the line's break-point". */
    [${POPOVER_ATTR}="dwell-swim"] {
      position: fixed;
      /* Wider than the sprite; the extra area is used by the eraser
         strip that covers the outline behind the duck. */
      width: 56px;
      height: 38px;
      pointer-events: none;
      z-index: var(--dddk-z-bar, 9500);
      overflow: visible;
      /* Eraser strip: horizontal white bar centred vertically in the
         container, fades out at left/right so the outline reappears
         smoothly. Positioned via background-* so it doesn't need an
         extra element and always tracks the container. */
      background:
        linear-gradient(
          90deg,
          transparent 0%,
          var(--dddk-dwell-swim-eraser, #ffffff) 20%,
          var(--dddk-dwell-swim-eraser, #ffffff) 80%,
          transparent 100%
        );
      background-repeat: no-repeat;
      /* The strip is the height of the outline stroke plus a bit; JS
         reads outline width to size this dynamically. */
      background-size: 100% var(--dddk-dwell-swim-eraser-height, 7px);
      background-position: 50% var(--dddk-dwell-swim-eraser-y, 50%);
      /* Constant velocity is JS-set: --dddk-swim-duration = W_px / 30. */
      /* ease-in-out per full loop so the duck decelerates naturally
         at each end and the FLIP feels intentional rather than a
         hard stop. Linear made the turn read as "sudden stop then
         teleport back". */
      animation: dddkDwellSwimMove var(--dddk-swim-duration, 8s) ease-in-out infinite;
      /* JS sets --dddk-swim-range so the animation stops at the right
         boundary regardless of duck sprite width. */
    }
    [${POPOVER_ATTR}="dwell-swim-duck"] {
      position: absolute;
      /* Container height is 38px, sprite is 32px — sprite bottom sits
         6px above container bottom so the duck's water ripple aligns
         with the outline. Vertical centre of sprite = container centre.
         Horizontally CENTRED in the shell so the eraser gap is
         symmetric on head side and tail side (was left-anchored → all
         the whitespace bunched behind the tail). */
      top: 3px;
      left: 50%;
      margin-left: -20px;
      width: 40px;
      height: 32px;
      /* Spritesheet: 8 frames × 256px wide, at 40px display width each
         frame is 40px. Total sheet display width = 320px. Doubling
         from 4 → 8 frames removes the stop-motion staccato feel. */
      background-image: var(--dddk-swim-cycle-url, none);
      background-size: 320px 32px;
      background-repeat: no-repeat;
      /* Frame cycle: 8 frames at ~12 fps × 2 (ping-pong via alternate)
         = 1.33s total loop. The LLM-generated frames aren't a perfect
         circular cycle — frame 8 back to frame 1 was a jarring jump.
         Ping-pong (0→7→0) uses each endpoint as a natural pause where
         the ease-in-out timing decelerates, so the transitions land
         smoothly instead of teleporting. */
      animation: dddkDwellSwimFrames 0.66s steps(8) infinite alternate;
      filter: drop-shadow(0 2px 3px rgba(0,60,100,0.18));
    }
    @keyframes dddkDwellSwimFrames {
      from { background-position: 0 0; }
      to   { background-position: -320px 0; }
    }
    @keyframes dddkDwellSwimMove {
      /* Sprite naturally faces LEFT. scaleX(-1) flips to face right. */
      0%   { transform: translateX(0) scaleX(-1); }
      48%  { transform: translateX(var(--dddk-swim-range, 100px)) scaleX(-1); }
      52%  { transform: translateX(var(--dddk-swim-range, 100px)) scaleX(1); }
      100% { transform: translateX(0) scaleX(1); }
    }
    @media (prefers-reduced-motion: reduce) {
      [${POPOVER_ATTR}="dwell-swim"] { animation: none; opacity: 0.7; }
      [${POPOVER_ATTR}="dwell-swim-duck"] { animation: none; }
    }

    /* ── Chill mascot in the bottom-right corner of the framed target
       — a side-view duck wearing sunglasses, statically perched. No
       animation (that's the whole vibe: the swim duck is doing the
       work, this one is on vacation). JS positions it via inline
       top/left each rAF tick so it stays glued to the target rect. */
    [${POPOVER_ATTR}="dwell-chill"] {
      position: fixed;
      width: 42px;
      height: 34px;
      pointer-events: none;
      z-index: var(--dddk-z-bar, 9500);
      background-image: var(--dddk-chill-url, none);
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      filter: drop-shadow(0 2px 3px rgba(0,60,100,0.18));
      /* Very slow, near-imperceptible float — reads as breath rather
         than motion. Add ONE frame per second so it's not a corpse. */
      animation: dddkDwellChillBreath var(--dddk-dwell-chill-breath-duration, 3.6s) ease-in-out infinite;
    }
    @keyframes dddkDwellChillBreath {
      0%, 100% { transform: translateY(0); }
      50%      { transform: translateY(-1.5px); }
    }
    @media (prefers-reduced-motion: reduce) {
      [${POPOVER_ATTR}="dwell-chill"] { animation: none; }
    }

    /* The target frame — applied to the long-pressed element. Dual
       outline: outer warm-yellow (thick) + inner navy dashed via a
       box-shadow inset. Reads as "picked out and framed by the duck"
       — the outer ring pulses gently so the frame feels alive rather
       than static. Fully themeable via these tokens. */
    [data-dddk-dwell-target] {
      outline:
        var(--dddk-dwell-frame-width, 3px)
        var(--dddk-dwell-frame-style, solid)
        var(--dddk-dwell-frame-color, var(--dddk-dwell-outer, #ffd93d));
      outline-offset: var(--dddk-dwell-frame-offset, 7px);
      border-radius: var(--dddk-dwell-frame-radius, var(--dddk-radius-sm, 6px));
      transition: outline-color 120ms ease-out;
      /* No inner navy stroke (was box-shadow inset — read as a second
         competing ring). No breathe/pulse on outline-offset — the
         frame stays fixed, only the swimming duck moves. */
    }
    @media (prefers-reduced-motion: reduce) {
      [data-dddk-dwell-target] { animation: none; }
      [${POPOVER_ATTR}="dwell"] .dwell-head::before { animation: none; }
    }
  `);
}
